import React, { useState, useEffect } from 'react';
import { 
    collection, query, getDocs, doc, updateDoc, 
    writeBatch, getDoc, setDoc, onSnapshot 
} from 'firebase/firestore';
import { useFirebase } from '../App';

const AdminDashboardPage = () => {
    const { db, isAdmin } = useFirebase();
    
    // Data State
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, delegates: 0, teachers: 0, staff: 0 });
    
    // UI State
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null); // The user being viewed
    const [extraData, setExtraData] = useState(null); // The stepper data (preferences/health)
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Settings State
    const [paperSubmissionOpen, setPaperSubmissionOpen] = useState(false);

    // --- 1. Fetch All Users ---
    useEffect(() => {
        if (!isAdmin) return;

        // Listener for App Settings (Toggle)
        const unsubSettings = onSnapshot(doc(db, 'appSettings', 'submissionSettings'), (doc) => {
            if (doc.exists()) setPaperSubmissionOpen(doc.data().positionPaperSubmissionsOpen);
        });

        const fetchUsers = async () => {
            try {
                // Fetch base users
                const q = query(collection(db, "users"));
                const querySnapshot = await getDocs(q);
                
                // Process users and fetch sub-collection data for delegates
                const usersData = await Promise.all(querySnapshot.docs.map(async docSnap => {
                    const userData = { id: docSnap.id, ...docSnap.data() };
                    
                    // If delegate, merge with subcollection info (like committee allocation)
                    if (userData.userType.includes('delegate')) {
                        const delSnap = await getDoc(doc(db, `users/${userData.id}/thessmunDelegateInfo`, 'myDelegateInfo'));
                        if (delSnap.exists()) {
                            return { ...userData, ...delSnap.data() }; // Merge
                        }
                    }
                    return userData;
                }));

                setUsers(usersData);
                
                // Calculate Stats
                setStats({
                    total: usersData.length,
                    delegates: usersData.filter(u => u.userType.includes('delegate')).length,
                    teachers: usersData.filter(u => u.userType === 'teacher').length,
                    staff: usersData.filter(u => u.userType === 'staff/chair').length
                });

                setLoading(false);
            } catch (error) {
                console.error("Error loading users:", error);
                setLoading(false);
            }
        };

        fetchUsers();
        return () => unsubSettings();
    }, [db, isAdmin]);

    // --- 2. Actions ---
    const togglePaperSubmissions = async () => {
        try {
            const newState = !paperSubmissionOpen;
            await setDoc(doc(db, 'appSettings', 'submissionSettings'), { positionPaperSubmissionsOpen: newState });
        } catch (err) {
            console.error(err);
        }
    };

    const handleViewDetails = async (user) => {
        setSelectedUser(user);
        setExtraData(null); // Reset previous data
        setIsModalOpen(true);

        // Lazy load the "Final Registration" (Stepper) data
        if (user.userType.includes('delegate')) {
            try {
                const finalSnap = await getDoc(doc(db, `users/${user.id}/thessmunDelegateInfo`, 'finalRegistration'));
                if (finalSnap.exists()) {
                    setExtraData(finalSnap.data());
                }
            } catch (err) {
                console.error("Could not fetch extra details", err);
            }
        }
    };

    const handleUpdateUser = async (updatedData) => {
        // Optimistic Update
        setUsers(users.map(u => u.id === selectedUser.id ? { ...u, ...updatedData } : u));
        setIsModalOpen(false);

        try {
            const batch = writeBatch(db);
            
            // 1. Update Root User Doc
            const userRef = doc(db, 'users', selectedUser.id);
            batch.update(userRef, updatedData);

            // 2. Update Delegate Subcollection (Keep sync)
            if (selectedUser.userType.includes('delegate')) {
                const delRef = doc(db, `users/${selectedUser.id}/thessmunDelegateInfo`, 'myDelegateInfo');
                batch.update(delRef, updatedData);
            }

            await batch.commit();
        } catch (err) {
            console.error("Update failed:", err);
            alert("Failed to save changes.");
        }
    };

    // --- 3. Filtering ---
    const filteredUsers = users
        .filter(u => filter === 'all' || u.userType === filter)
        .filter(u => 
            u.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            u.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // --- Render Helpers ---
    const StatusBadge = ({ status }) => {
        const colors = {
            Approved: 'bg-green-100 text-green-800',
            Rejected: 'bg-red-100 text-red-800',
            Waitlisted: 'bg-yellow-100 text-yellow-800',
            Pending: 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${colors[status] || colors.Pending}`}>
                {status || 'Pending'}
            </span>
        );
    };

    if (loading) return <div className="p-10 text-center animate-pulse">Loading Admin Dashboard...</div>;
    if (!isAdmin) return <div className="p-10 text-center text-red-600">Access Denied</div>;

    return (
        <section className="container mx-auto px-4 py-8 animate-fade-in" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-body-text)' }}>
            
            {/* Top Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <StatCard title="Total Users" count={stats.total} />
                <StatCard title="Delegates" count={stats.delegates} color="blue" />
                <StatCard title="Teachers" count={stats.teachers} color="purple" />
                <StatCard title="Staff" count={stats.staff} color="orange" />
            </div>

            {/* Controls Row */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded shadow" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                {/* Search & Filter */}
                <div className="flex gap-2 w-full md:w-auto">
                    <input 
                        type="text" 
                        placeholder="Search users..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 border rounded text-black w-full"
                    />
                    <select value={filter} onChange={(e) => setFilter(e.target.value)} className="p-2 border rounded text-black">
                        <option value="all">All Roles</option>
                        <option value="individual_delegate">Delegates</option>
                        <option value="teacher">Teachers</option>
                        <option value="staff/chair">Staff</option>
                    </select>
                </div>

                {/* Global Toggles */}
                <div className="flex items-center gap-4">
                    <span className="text-sm font-bold">Paper Submissions:</span>
                    <button 
                        onClick={togglePaperSubmissions}
                        className={`px-4 py-1 rounded-full font-bold text-sm text-white ${paperSubmissionOpen ? 'bg-green-600' : 'bg-red-600'}`}
                    >
                        {paperSubmissionOpen ? 'OPEN' : 'CLOSED'}
                    </button>
                </div>
            </div>

            {/* Main User Table */}
            <div className="bg-white rounded shadow overflow-hidden" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                <table className="w-full text-left">
                    <thead className="bg-gray-100 border-b" style={{ backgroundColor: 'var(--color-section1-icon-bg)', color: 'var(--color-section1-heading)' }}>
                        <tr>
                            <th className="p-4">Name</th>
                            <th className="p-4">Role</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Allocation</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {filteredUsers.map(user => (
                            <tr key={user.id} className="hover:bg-gray-50 transition-colors" style={{ borderColor: 'var(--color-section1-card-border)' }}>
                                <td className="p-4 font-medium">{user.fullName}</td>
                                <td className="p-4 text-sm opacity-75">{user.userType}</td>
                                <td className="p-4"><StatusBadge status={user.registrationStatus} /></td>
                                <td className="p-4 text-sm">
                                    {user.committeeAllocation ? (
                                        <span>{user.committeeAllocation} / <strong>{user.countryAllocation}</strong></span>
                                    ) : <span className="text-gray-400">-</span>}
                                </td>
                                <td className="p-4 text-right">
                                    <button 
                                        onClick={() => handleViewDetails(user)}
                                        className="text-blue-600 hover:text-blue-800 font-bold text-sm border border-blue-600 px-3 py-1 rounded hover:bg-blue-50"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* USER DETAILS MODAL */}
            {isModalOpen && selectedUser && (
                <UserDetailModal 
                    user={selectedUser} 
                    extraData={extraData}
                    onClose={() => setIsModalOpen(false)} 
                    onSave={handleUpdateUser} 
                />
            )}
        </section>
    );
};

// --- Sub-Components ---

const StatCard = ({ title, count, color = 'gray' }) => (
    <div className={`p-4 bg-white rounded shadow border-l-4 border-${color}-500`} style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
        <p className="text-gray-500 text-xs uppercase">{title}</p>
        <p className="text-2xl font-bold">{count}</p>
    </div>
);

const UserDetailModal = ({ user, extraData, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        registrationStatus: user.registrationStatus || 'Pending',
        committeeAllocation: user.committeeAllocation || '',
        countryAllocation: user.countryAllocation || ''
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col" style={{ backgroundColor: 'var(--color-section1-card-bg)', color: 'var(--color-body-text)' }}>
                
                {/* Modal Header */}
                <div className="p-6 border-b flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold">{user.fullName}</h2>
                        <p className="text-sm opacity-75">{user.email}</p>
                        <p className="text-xs text-blue-600 font-mono mt-1">{user.id}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>

                <div className="p-6 space-y-6">
                    {/* 1. Admin Actions Section */}
                    <div className="bg-gray-50 p-4 rounded border" style={{ backgroundColor: 'var(--color-section1-icon-bg)' }}>
                        <h3 className="font-bold mb-4 text-blue-800">Admin Actions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold uppercase mb-1">Status</label>
                                <select 
                                    className="w-full p-2 border rounded text-black"
                                    value={formData.registrationStatus}
                                    onChange={e => setFormData({...formData, registrationStatus: e.target.value})}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Approved">Approved</option>
                                    <option value="Waitlisted">Waitlisted</option>
                                    <option value="Rejected">Rejected</option>
                                </select>
                            </div>
                            {user.userType.includes('delegate') && (
                                <>
                                    <div>
                                        <label className="block text-xs font-bold uppercase mb-1">Committee</label>
                                        <input 
                                            className="w-full p-2 border rounded text-black"
                                            value={formData.committeeAllocation}
                                            onChange={e => setFormData({...formData, committeeAllocation: e.target.value})}
                                            placeholder="e.g. DISEC"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase mb-1">Country</label>
                                        <input 
                                            className="w-full p-2 border rounded text-black"
                                            value={formData.countryAllocation}
                                            onChange={e => setFormData({...formData, countryAllocation: e.target.value})}
                                            placeholder="e.g. France"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* 2. Documents Section */}
                    <div className="grid grid-cols-2 gap-4">
                        {user.cvUrl && (
                            <a href={user.cvUrl} target="_blank" rel="noreferrer" className="block p-3 border rounded text-center hover:bg-gray-50 text-blue-600">
                                📄 View Staff CV
                            </a>
                        )}
                        {user.positionPaperUrl && (
                            <a href={user.positionPaperUrl} target="_blank" rel="noreferrer" className="block p-3 border rounded text-center hover:bg-gray-50 text-blue-600">
                                📝 View Position Paper
                            </a>
                        )}
                    </div>

                    {/* 3. Stepper Data (Preferences & Health) */}
                    {extraData ? (
                        <div className="border-t pt-4">
                            <h3 className="font-bold mb-3 text-lg">Delegate Preferences (From Registration)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="p-3 bg-blue-50 rounded">
                                    <p className="font-bold text-blue-800">Committee Preferences</p>
                                    <ol className="list-decimal pl-4 mt-1">
                                        <li>{extraData.preferences?.committee?.[0] || '-'}</li>
                                        <li>{extraData.preferences?.committee?.[1] || '-'}</li>
                                    </ol>
                                </div>
                                <div className="p-3 bg-blue-50 rounded">
                                    <p className="font-bold text-blue-800">Country Preferences</p>
                                    <ol className="list-decimal pl-4 mt-1">
                                        <li>{extraData.preferences?.country?.[0] || '-'}</li>
                                        <li>{extraData.preferences?.country?.[1] || '-'}</li>
                                    </ol>
                                </div>
                                <div className="md:col-span-2 p-3 bg-red-50 rounded">
                                    <p className="font-bold text-red-800">Health & Dietary</p>
                                    <p className="mt-1"><span className="font-semibold">Food:</span> {extraData.health?.food || 'None'}</p>
                                    <p className="mt-1"><span className="font-semibold">Medical:</span> {extraData.health?.medical || 'None'}</p>
                                    <p className="mt-1"><span className="font-semibold">Notes:</span> {extraData.health?.notes || 'None'}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        user.userType.includes('delegate') && (
                            <div className="p-4 text-center text-gray-400 italic border rounded border-dashed">
                                Delegate has not completed the Final Registration Steps yet.
                            </div>
                        )
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3" style={{ backgroundColor: 'var(--color-section1-icon-bg)' }}>
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-black">Cancel</button>
                    <button 
                        onClick={() => onSave(formData)} 
                        className="px-6 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 shadow"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;